<template>
    <div id="pdf-uploader">
        <div
            @dragover="event => event.preventDefault()"
            @drop.prevent="onDrop"
            @click="uploadFile"
            class="upload-box mb-2"
        >
            <i :class="`${renderIcon} text-2xl mb-2`"></i>
            <div>
                <span v-if="fileName">{{ fileName }}</span>
                <span v-else>{{ translate('robot_modal_upload_file_accept', 'Upload File (.pdf, .docx, .txt)') }}</span>
            </div>
        </div>

        <div v-if="pages.length" class="pdf-box">
            <div
                v-for="(page, index) in pages"
                @click="selectPage(page)"
                :key="index"
                class="m-2"
                style="position: relative"
            >
                <i @click.stop="removePage(page)" class="remove-icon fas fa-times"></i>
                <img
                    :src="page.src"
                    :class="{
                        'pdf-image': true,
                        active: isActive(page),
                        'not-selected': !isActive(page) && pageSelected.length === limitPages
                    }"
                    alt="PDF Page as Image"
                />
                <p class="text-xs text-center m-0">
                    {{ translate('robot_modal_upload_pdf_page', 'Page') }} {{ page.page }}
                </p>
            </div>
        </div>
        <!-- just hidden -->
        <pdf v-show="false" :src="pdfData" ref="myPdfComponent"></pdf>
    </div>
</template>

<script>
import pdf from 'vue-pdf'

export default {
    components: {
        pdf
    },

    data() {
        return {
            pdfData: null,
            pageSelected: [],
            pages: [],
            fileName: '',
            limit: {
                teacher_plus: 15,
                member: 5
            }
        }
    },

    computed: {
        isBackend() {
            return process.env.VUE_APP_IS_BACKEND
        },

        renderIcon() {
            if (!this.fileName) {
                return 'fas fa-upload'
            }

            return this.fileName.includes('.docx')
                ? 'far fa-file-word'
                : this.fileName.includes('.txt')
                ? 'far fa-file-alt'
                : 'far fa-file-pdf'
        },

        limitPages() {
            if (this.isBackend) {
                return 10
            }

            const user = this.getUser()

            return this.limit[user.role] || this.limit.member
        }
    },

    methods: {
        translate(key, msg) {
            if (this.isBackend) {
                return msg
            }

            return this.$t(key)
        },

        isActive(page) {
            return this.pageSelected.some(selectedPage => selectedPage.page === page.page)
        },

        uploadFile() {
            const input = document.createElement('input')
            input.type = 'file'
            input.accept = '.pdf,.docx,.txt'
            input.addEventListener('change', async () => {
                const file = input.files[0]
                await this.checkFileType(file)
            })

            input.click()
        },

        async onDrop(event) {
            event.preventDefault()
            const file = event.dataTransfer.files[0]
            await this.checkFileType(file)
        },

        async checkFileType(file) {
            this.$emit('start-upload')

            if (file) {
                if (file.type === 'application/pdf') {
                    await this.uploadPDF(file)
                } else if (file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
                    this.uploadDocx(file)
                } else if (file.type === 'text/plain') {
                    this.uploadTxt(file)
                } else {
                    this.$emit('end-upload')

                    if (this.isBackend) {
                        window.shout.error('File type is invalid')
                    } else {
                        this.toastError(this.$t('robot_modal_upload_invalid_file'))
                    }
                }
            }
        },

        uploadDocx(file) {
            this.reset()
            this.fileName = file.name
            this.$emit('file', 'docx', file)
            this.$emit('end-upload')
        },

        uploadTxt(file) {
            this.reset()
            this.fileName = file.name
            this.$emit('file', 'txt', file)
            this.$emit('end-upload')
        },

        async uploadPDF(file) {
            this.pdfData = pdf.createLoadingTask(URL.createObjectURL(file))
            this.pages = []
            this.pageSelected = []
            this.fileName = file.name

            try {
                const pdfDoc = await this.pdfData.promise

                // Process pages one by one
                for (let i = 1; i <= pdfDoc.numPages; i++) {
                    const page = await pdfDoc.getPage(i)
                    await this.renderPage(page)
                }

                this.$emit('file', 'pdf', this.pageSelected)
                this.$emit('end-upload')
            } catch (err) {
                this.$emit('end-upload')

                if (this.isBackend) {
                    window.shout.error('Upload file error')
                } else {
                    this.toastError(this.$t('robot_modal_upload_file_error'))
                }
            }
        },

        async renderPage(page) {
            const canvas = document.createElement('canvas')
            const viewport = page.getViewport({ scale: 0.5 })
            const context = canvas.getContext('2d')

            canvas.height = viewport.height
            canvas.width = viewport.width

            const renderContext = {
                canvasContext: context,
                viewport: viewport
            }

            try {
                const renderTask = page.render(renderContext)
                await renderTask.promise

                const content = await page.getTextContent()
                // Remove the first 3 objects ("\t\") and join to string
                const text = content.items
                    .map(item => item.str)
                    .slice(3)
                    .join('')

                const src = canvas.toDataURL('image/png')

                this.pages.push({ src, page: page.pageNumber, text })
                this.pageSelected = this.pages.slice(0, this.limitPages).map(page => ({
                    page: page.page,
                    text: page.text
                }))
            } catch (err) {
                if (this.isBackend) {
                    window.shout.error('Upload file error')
                } else {
                    this.toastError(this.$t('robot_modal_upload_file_error'))
                }
            } finally {
                canvas.remove()
            }
        },

        selectPage(page) {
            const isSelected = this.pageSelected.some(p => p.page === page.page)
            if (isSelected) {
                this.pageSelected = this.pageSelected.filter(p => p.page !== page.page)
            } else if (this.pageSelected.length < this.limitPages) {
                this.pageSelected.push({ page: page.page, text: page.text })
            }

            this.$emit('file', 'pdf', this.pageSelected)
        },

        removePage(page) {
            this.pages = this.pages.filter(p => p.page !== page.page)
            this.pageSelected = this.pageSelected.filter(p => p.page !== page.page)

            if (this.pages.length === 0) {
                this.reset()
                this.fileName = ''
            }

            this.$emit('file', 'pdf', this.pageSelected)
        },

        reset() {
            this.pdfData = null
            this.pageSelected = []
            this.pages = []
        }
    }
}
</script>

<style lang="scss" scoped>
#pdf-uploader {
    .upload-box {
        cursor: pointer;
        border-radius: 0.25rem;
        background-color: #e5e5e5;
        border: 1px solid #ccc;
        text-align: center;
        padding: 1.5rem;
        margin-top: 0.25rem;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
    }

    .badge-file-name {
        display: inline-block;
        padding: 0.25em 0.4em;
        font-size: 75%;
        font-weight: 700;
        line-height: 1;
        text-align: center;
        white-space: nowrap;
        vertical-align: baseline;
        border-radius: 0.25rem;
        color: #fff;
        background-color: #28a745;
    }

    .pdf-box {
        border: 1px solid #ccc;
        border-radius: 0.25rem;
        display: flex;
        overflow-x: auto;
        overflow-y: hidden;
        width: 100%;
        cursor: pointer;

        .pdf-image {
            width: 100px;
            height: 150px;
        }

        .not-selected {
            opacity: 0.4;
            cursor: not-allowed;
        }

        .remove-icon {
            position: absolute;
            width: 18px;
            height: 18px;
            top: -5px;
            right: -5px;
            cursor: pointer;
            font-size: 1rem;
            background-color: red;
            color: #fff;
            border-radius: 50%;
            display: flex;
            justify-content: center;
            align-items: center;
            padding-left: 1px;
            z-index: 1;
        }
    }

    .active {
        border: 2px solid #000;
    }
}
</style>
